/**
 * Add to Yaruta (YAR-556): what the browser does to a file before it is
 * uploaded — classify it by its magic bytes (never `file.type`), hash the
 * original for "already added", and shrink photos (Decision D6).
 *
 * The server re-checks every one of these; the browser checks first so a
 * refusal is instant and nothing is sent.
 */

/** A photo over this before shrinking is refused (the cut-off Kelvin asked for). */
export const PHOTO_MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;
/** Mirrors `MAX_PDF_BYTES` in `_shared/pdf-text.ts`. */
export const PDF_MAX_BYTES = 8 * 1024 * 1024;
/** Mirrors `OPEN_POLICY_MAX_IMAGE_BYTES`; the server enforces it. */
export const IMAGE_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const IMAGE_MAX_LONG_SIDE = 2048;

export type SniffedKind = 'jpeg' | 'png' | 'webp' | 'heic' | 'pdf' | null;

export type Classification =
  | { ok: true; kind: 'photo' | 'file'; sniffed: Exclude<SniffedKind, null> }
  | {
      ok: false;
      refusal: 'unsupported_type' | 'photo_too_large' | 'pdf_too_large';
    };

export class UploadPrepError extends Error {
  readonly code: 'unsupported_type';

  constructor(code: 'unsupported_type') {
    super(code);
    this.name = 'UploadPrepError';
    this.code = code;
  }
}

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
  'heif',
]);

function startsWith(head: Uint8Array, bytes: number[], at = 0): boolean {
  if (head.length < at + bytes.length) return false;
  return bytes.every((b, i) => head[at + i] === b);
}

function ascii(head: Uint8Array, from: number, to: number): string {
  return head.length < to
    ? ''
    : String.fromCharCode(...head.subarray(from, to));
}

export function sniffKind(head: Uint8Array): SniffedKind {
  if (startsWith(head, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
    return 'png';
  if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP')
    return 'webp';
  if (ascii(head, 0, 5) === '%PDF-') return 'pdf';
  if (ascii(head, 4, 8) === 'ftyp' && HEIC_BRANDS.has(ascii(head, 8, 12)))
    return 'heic';
  return null;
}

/**
 * The blob's bytes. `Blob.arrayBuffer` where the runtime has it (every
 * browser Yaruta supports); `FileReader` otherwise (jsdom).
 */
function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) resolve(result);
      else reject(new Error('read failed'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/** Reads the first 16 bytes; caps by kind. `file.type` plays no part. */
export async function classifyUploadFile(file: File): Promise<Classification> {
  const head = new Uint8Array(await readBytes(file.slice(0, 16)));
  const sniffed = sniffKind(head);
  if (sniffed === null) return { ok: false, refusal: 'unsupported_type' };
  if (sniffed === 'pdf') {
    return file.size > PDF_MAX_BYTES
      ? { ok: false, refusal: 'pdf_too_large' }
      : { ok: true, kind: 'file', sniffed };
  }
  return file.size > PHOTO_MAX_ORIGINAL_BYTES
    ? { ok: false, refusal: 'photo_too_large' }
    : { ok: true, kind: 'photo', sniffed };
}

/** SHA-256 of the blob's bytes, lowercase hex. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new Uint8Array(await readBytes(blob)),
  );
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}

export type DrawImage = (
  ctx: Pick<CanvasRenderingContext2D, 'drawImage'>,
  width: number,
  height: number,
) => void;

export interface DecodedImage {
  width: number;
  height: number;
  /** Safe to call any number of times: the re-encode ladder draws once per step. */
  draw: DrawImage;
  /** Frees the decoded pixels. `prepareImage` calls it exactly once, last. */
  close?: () => void;
}

/** The decode/encode seam: jsdom has no canvas, so tests inject a fake. */
export interface ImageCodec {
  decode(blob: Blob): Promise<DecodedImage>;
  encodeJpeg(
    width: number,
    height: number,
    draw: DrawImage,
    quality: number,
  ): Promise<Blob>;
}

function decodeWithImg(blob: Blob): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      // The object URL stays alive until the image is released.
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
        close: () => URL.revokeObjectURL(url),
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
}

/**
 * `createImageBitmap`, falling back to `<img>` (Safari decodes HEIC in an
 * `<img>` where `createImageBitmap` may refuse it), and an offscreen canvas.
 */
export const browserImageCodec: ImageCodec = {
  async decode(blob) {
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(blob);
        return {
          width: bitmap.width,
          height: bitmap.height,
          draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
          close: (() => {
            let closed = false;
            return () => {
              if (closed) return;
              closed = true;
              bitmap.close();
            };
          })(),
        };
      } catch {
        // fall through to <img>
      }
    }
    return decodeWithImg(blob);
  },
  encodeJpeg(width, height, draw, quality) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('no 2d context'));
        return;
      }
      draw(ctx, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
        'image/jpeg',
        quality,
      );
    });
  },
};

/** Re-encode attempts, in order, until the result is ≤ 2 MiB (Decision D6). */
const ATTEMPTS: ReadonlyArray<{ longSide: number; quality: number }> = [
  { longSide: IMAGE_MAX_LONG_SIDE, quality: 0.85 },
  { longSide: IMAGE_MAX_LONG_SIDE, quality: 0.7 },
  { longSide: 1600, quality: 0.7 },
];

const PASS_THROUGH_EXT = { png: 'png', webp: 'webp' } as const;

/**
 * A PNG/WebP already ≤ 2048 px and ≤ 2 MiB goes up untouched, so text
 * screenshots stay lossless. Anything else — every JPEG (a camera photo
 * carries EXIF, GPS included; re-encoding through a canvas drops it) and
 * every HEIC — is re-encoded as JPEG. Throws `UploadPrepError('unsupported_type')` when the browser
 * cannot decode it (HEIC outside Safari) or cannot encode.
 */
export async function prepareImage(
  file: File,
  sniffed: 'jpeg' | 'png' | 'webp' | 'heic',
  codec: ImageCodec = browserImageCodec,
): Promise<{ blob: Blob; ext: 'jpg' | 'png' | 'webp'; reencoded: boolean }> {
  let decoded: DecodedImage;
  try {
    decoded = await codec.decode(file);
  } catch {
    throw new UploadPrepError('unsupported_type');
  }

  try {
    return await fitImage(file, sniffed, decoded, codec);
  } finally {
    decoded.close?.();
  }
}

async function fitImage(
  file: File,
  sniffed: 'jpeg' | 'png' | 'webp' | 'heic',
  decoded: DecodedImage,
  codec: ImageCodec,
): Promise<{ blob: Blob; ext: 'jpg' | 'png' | 'webp'; reencoded: boolean }> {
  const longSide = Math.max(decoded.width, decoded.height);
  if (
    (sniffed === 'png' || sniffed === 'webp') &&
    file.size <= IMAGE_MAX_UPLOAD_BYTES &&
    longSide <= IMAGE_MAX_LONG_SIDE
  ) {
    return { blob: file, ext: PASS_THROUGH_EXT[sniffed], reencoded: false };
  }

  let blob: Blob | null = null;
  for (const { longSide: target, quality } of ATTEMPTS) {
    const scale = Math.min(1, target / longSide);
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));
    try {
      blob = await codec.encodeJpeg(width, height, decoded.draw, quality);
    } catch {
      throw new UploadPrepError('unsupported_type');
    }
    if (blob.size <= IMAGE_MAX_UPLOAD_BYTES) break;
  }
  // Still over 2 MiB after the last attempt: send it anyway; the server's
  // 2 MiB ceiling refuses it as too large.
  if (!blob) throw new UploadPrepError('unsupported_type');
  return { blob, ext: 'jpg', reencoded: true };
}
